import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  IconButton,
  Tooltip,
  useToast,
} from '@chakra-ui/react'
import { FiClock, FiTrash2, FiArrowLeft } from 'react-icons/fi'
import { containerVariants, listItemVariants } from '@/theme/animations'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useRecentlyViewed } from '@/hooks/useRecentlyViewed'
import { MotionBox } from '@/components/Common/MotionBox'
import FileTable from '@/components/FileExplorer/FileTable'

interface RecentlyViewedExplorerProps {
  onBack: () => void
}

export default function RecentlyViewedExplorer({ onBack }: RecentlyViewedExplorerProps) {
  const { items: recentlyViewed, isLoading, error, clear } = useRecentlyViewed({
    limit: 50,
    loadOnMount: true,
  })
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])

  const handleClearAll = async () => {
    try {
      await clear()
      toast({
        title: t('recentlyViewed.cleared', { ns: 'files' }),
        description: t('recentlyViewed.clearedDescription', { ns: 'files' }),
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
    } catch {
      toast({
        title: t('status.error', { ns: 'common' }),
        description: t('recentlyViewed.clearError', { ns: 'files' }),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  if (isLoading) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <Text color="gray.600">{t('recentlyViewed.loading', { ns: 'files' })}</Text>
          </VStack>
        </Center>
      </MotionBox>
    )
  }

  if (error) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <Alert status="error" borderRadius="lg" maxW="md">
            <AlertIcon />
            <VStack spacing={2} align="start">
              <Text fontWeight="medium">{t('recentlyViewed.errorLoading', { ns: 'files' })}</Text>
              <Text fontSize="sm">{error}</Text>
            </VStack>
          </Alert>
        </Center>
      </MotionBox>
    )
  }

  return (
    <MotionBox
      initial="initial"
      animate="animate"
      variants={containerVariants}
      p={6}
      h="full"
      overflow="auto"
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <IconButton
              aria-label={t('buttons.back', { ns: 'common' })}
              icon={<FiArrowLeft />}
              size="sm"
              variant="ghost"
              onClick={onBack}
            />
            <HStack spacing={2}>
              <FiClock size="20px" color="#4299E1" />
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                {t('recentlyViewed.title', { ns: 'files' })}
              </Text>
            </HStack>
          </HStack>

          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.600">
              {t('plurals.file', { ns: 'common', count: recentlyViewed.length })}
            </Text>
            {recentlyViewed.length > 0 && (
              <Tooltip label={t('recentlyViewed.clearAll', { ns: 'files' })} placement="top">
                <IconButton
                  aria-label={t('recentlyViewed.clearAll', { ns: 'files' })}
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  color="gray.500"
                  onClick={handleClearAll}
                  _hover={{ color: 'red.500', bg: 'red.50' }}
                />
              </Tooltip>
            )}
          </HStack>
        </HStack>

        {/* Files Table */}
        {recentlyViewed.length === 0 ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Box color="gray.400">
                <FiClock size="48px" />
              </Box>
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {t('recentlyViewed.empty', { ns: 'files' })}
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  {t('recentlyViewed.emptyDescription', { ns: 'files' })}
                </Text>
              </VStack>
            </VStack>
          </Center>
        ) : (
          <MotionBox variants={listItemVariants}>
            {/* Same table as the file explorer. initialSort={null} keeps the list
                in most-recently-viewed order until a header is clicked; a third
                click on that header returns to recency. */}
            <FileTable
              files={recentlyViewed.map((record) => record.file)}
              preferenceScope="recent"
              initialSort={null}
              enablePagination={false}
            />
          </MotionBox>
        )}
      </VStack>
    </MotionBox>
  )
}
